import { updateTrackingProperties } from './helper';
import * as v3 from './v3';
import * as v4 from './v4';
import { triggerDeferredConversion } from './v4.visitor-properties';
import { isV3 } from '../tracking-version';

export { triggerDeferredConversion };

import type {
    TrackAnswerClickArgs,
    TrackFormSubmitArgs,
    TrackMultipleChoiceClickArgs,
} from '$lib/types/tracking';

export const trackButtonClick = (buttonComponentId: string, buttonContent: { clickId: string }) => {
    if (isV3()) {
        v3.trackButtonClick(buttonComponentId);

        return;
    }

    v4.buttonClick(buttonContent.clickId);
};

export const trackImageClick = (imageComponentId: string, clickId: string) => {
    if (isV3()) {
        v3.trackImageClick(imageComponentId);

        return;
    }

    v4.imageClick(clickId);
};

export const trackFormSubmit = ({
    form,
    values,
    fields,
    button,
    inputMap,
    suppressConversion,
}: TrackFormSubmitArgs) => {
    if (isV3()) {
        v3.trackFormSubmit({ formId: form._id, formValues: values, fields, inputMap });
        v3.trackButtonClick(button?._id);

        return;
    }

    // Tracking V4 requires the field name to be mapped to the value
    const mappedValues: { [fieldName: string]: string } = {};

    Object.keys(values).forEach((key) => {
        const value = values[key];
        const inputId = inputMap.byName[form._id][key];
        const input = form.components.find((input) => {
            return inputId === input._id;
        });

        if (input) {
            mappedValues[input.content.fieldName] = value;
        }
    });

    // if fullAddress is in the values, add the street, city, and country to the mappedValues
    if (values.fullAddress && values.address) {
        mappedValues['address.street'] = values.address.street;
        mappedValues['address.city'] = values.address.city;
        mappedValues['address.country'] = values.address.country;
        mappedValues['address.postalCode'] = values.address.postalCode;
    }

    // Update trackingProperties with the form values for personalization
    updateTrackingProperties({
        form,
        values,
        fields,
        inputMap,
    });

    v4.formSubmit(mappedValues, button?.content.clickId, { suppressConversion });
};

export const trackMultipleChoiceClick = ({
    answers,
    question,
    fields,
    button,
    campaignId,
}: TrackMultipleChoiceClickArgs) => {
    if (isV3()) {
        const answerIds = answers.map(({ id }) => id);
        v3.trackMultipleChoiceClick({ answerIds, questionId: question.id, fields });
        v3.trackButtonClick(button?._id);

        return;
    }

    v4.multipleChoiceSubmit(
        {
            text: question.content.plainText,
            fieldName: question.content.fieldName,
        },
        {
            text: answers
                .map(({ plainText }) => plainText)
                .sort()
                .join('; '),
            clickId: button?.content?.clickId || '',
        },
        campaignId,
    );
};

export const trackAnswerClick = ({ question, answer, fields }: TrackAnswerClickArgs) => {
    if (isV3()) {
        v3.trackAnswerClick({ answerId: answer?._id, questionId: question.id, fields });
        v3.trackButtonClick(answer?._id);

        return;
    }

    const questionDetails = {
        text: question.content.plainText,
        fieldName: question.content.fieldName,
    };

    // Tracked answer can be media or text answer
    const answerDetails = {
        text:
            answer?.componentType === 'questionMediaAnswer'
                ? answer?.components[1].content.plainText
                : answer?.content.plainText,
        clickId: answer?.content.clickId,
    };

    v4.answerClicked(questionDetails, answerDetails, answer?.campaignId);
};

export const trackCalendly = () => {
    if (isV3()) {
        v3.trackCalendly();

        return;
    }

    v4.calendlyBooking();
};

export const trackCal = () => {
    if (isV3()) {
        v3.trackCal();

        return;
    }

    v4.calBooking();
};

export const trackGHL = () => {
    if (isV3()) {
        v3.trackGHL();

        return;
    }

    v4.ghlBooking();
};

export const trackHubSpot = () => {
    if (isV3()) {
        v3.trackHubSpot();

        return;
    }

    v4.hubspotBooking();
};

export const trackPage = (pageData: {
    pageName: string;
    tag: string;
    campaignId: string;
    pageSlug: string;
    isLastPage: boolean;
    pageId: string;
    fromPageId: string | null;
    fromPageName: string | null;
}) => {
    if (isV3()) {
        v3.trackPage(
            {
                name: pageData.pageName,
                tag: pageData.tag,
                campaignId: pageData.campaignId,
                slug: pageData.pageSlug,
            },
            pageData.isLastPage,
        );

        return;
    }

    v4.pageLoad(
        pageData.pageId,
        pageData.pageName,
        pageData.pageSlug,
        pageData.isLastPage,
        pageData.fromPageId,
        pageData.fromPageName,
    );
};

export const trackVariant = (pageName: string) => {
    if (isV3()) {
        v3.trackVariant(pageName);

        return;
    }
};

export const trackCookies = (integrations: string[]) => {
    if (isV3()) {
        v3.trackCookies(integrations);

        return;
    }

    v4.cookiesAccept(integrations);
};
